import React, { useState, useEffect, useRef } from 'react';
import { getVehicles, getVehicleStatus, getTrack } from '../utils/api';
import axios from 'axios';

const fmt = d => d.toISOString().replace('T',' ').substring(0,16);
const today = new Date();
const todayStart = new Date(today); todayStart.setHours(0,0,0,0);

// Leaflet is loaded via CDN in useEffect
let L = null;

export default function MapPage() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const routeLayerRef = useRef(null);
  const [vehicles, setVehicles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('live'); // live | route
  const [startTime, setStartTime] = useState(fmt(todayStart));
  const [endTime, setEndTime] = useState(fmt(today));
  const [tracks, setTracks] = useState([]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [playbackIdx, setPlaybackIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const playIntervalRef = useRef(null);

  // Load Leaflet CSS + JS dynamically
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.css';
      document.head.appendChild(link);
    }
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.min.js';
      script.onload = () => { L = window.L; initMap(); };
      document.head.appendChild(script);
    } else {
      L = window.L; initMap();
    }
    return () => { if (playIntervalRef.current) clearInterval(playIntervalRef.current); };
  }, []);

  const initMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = window.L.map(mapRef.current, { center: [3.1390, 101.6869], zoom: 11, zoomControl: true });
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors', maxZoom: 19
    }).addTo(map);
    mapInstanceRef.current = map;
    setMapReady(true);
  };

  // Load vehicles + live positions
  useEffect(() => {
    loadPositions();
    const interval = setInterval(loadPositions, 15000); // refresh every 15s
    return () => clearInterval(interval);
  }, []);

  const loadPositions = async () => {
    try {
      const [vRes, pRes] = await Promise.all([
        getVehicles(),
        axios.get('/api/vehicles/positions', { params: { jsession: window.__mdvrAuth?.jsession, account: window.__mdvrAuth?.account } })
      ]);
      setVehicles(vRes.vehicles || []);
      setPositions(pRes.data.positions || []);
    } catch {}
  };

  // Update markers on map when positions change
  useEffect(() => {
    if (!mapReady || !window.L || positions.length === 0) return;
    const map = mapInstanceRef.current;

    positions.forEach(pos => {
      if (!pos.lat || !pos.lng) return;
      const id = pos.vehiIdno;
      const color = pos.online ? (pos.speed > 0 ? '#185FA5' : '#4CAF50') : '#E24B4A';

      const icon = window.L.divIcon({
        className: '',
        html: `<div style="
          width:32px; height:32px; background:${color}; border-radius:50% 50% 50% 0;
          transform:rotate(-45deg); border:2px solid white;
          display:flex; align-items:center; justify-content:center;
          box-shadow:0 2px 8px rgba(0,0,0,.3);">
          <div style="transform:rotate(45deg); color:white; font-size:8px; font-weight:700; text-align:center; line-height:1.1">
            ${pos.speed > 0 ? Math.round(pos.speed) : ''}<br/>${pos.speed > 0 ? 'km/h' : ''}
          </div>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });

      const popup = `
        <div style="font-family:system-ui,sans-serif;font-size:12px;min-width:160px">
          <div style="font-weight:500;margin-bottom:6px;font-size:13px">${id}</div>
          <div style="color:#888;margin-bottom:2px">Status: <span style="color:${color};font-weight:500">${pos.online ? 'Online' : 'Offline'}</span></div>
          <div style="color:#888;margin-bottom:2px">Speed: ${Math.round(pos.speed)} km/h</div>
          <div style="color:#888;margin-bottom:2px">Time: ${pos.gpsTime || '—'}</div>
          ${pos.location ? `<div style="color:#aaa;font-size:10px;margin-top:4px">${pos.location}</div>` : ''}
        </div>`;

      if (markersRef.current[id]) {
        markersRef.current[id].setLatLng([pos.lat, pos.lng]).setIcon(icon).getPopup()?.setContent(popup);
      } else {
        const marker = window.L.marker([pos.lat, pos.lng], { icon }).addTo(map).bindPopup(popup);
        markersRef.current[id] = marker;
      }
    });

    // Auto-fit bounds if first load
    if (Object.keys(markersRef.current).length > 0) {
      const group = window.L.featureGroup(Object.values(markersRef.current));
      if (group.getBounds().isValid()) map.fitBounds(group.getBounds(), { padding: [40, 40] });
    }
  }, [positions, mapReady]);

  // Load and draw route
  const loadRoute = async () => {
    if (!selected) return;
    setLoadingRoute(true); setTracks([]); setPlaybackIdx(0); setPlaying(false);
    if (playIntervalRef.current) clearInterval(playIntervalRef.current);

    // Clear old route
    if (routeLayerRef.current) { routeLayerRef.current.remove(); routeLayerRef.current = null; }

    try {
      const s = startTime.replace('T',' ')+':00';
      const e = endTime.replace('T',' ')+':00';
      const data = await getTrack(selected, s, e);
      const pts = (data.tracks || []).filter(t => t.lat && t.lng);
      setTracks(pts);

      if (pts.length > 0 && mapInstanceRef.current) {
        const map = mapInstanceRef.current;
        const latlngs = pts.map(t => [parseFloat(t.lat), parseFloat(t.lng)]);

        // Draw route polyline
        const line = window.L.polyline(latlngs, { color:'#185FA5', weight:3, opacity:.8 }).addTo(map);

        // Start marker (green)
        window.L.circleMarker(latlngs[0], { radius:8, color:'white', fillColor:'#4CAF50', fillOpacity:1, weight:2 })
          .addTo(map).bindPopup(`<b>Start</b><br/>${pts[0].gpsTime || ''}`);

        // End marker (red)
        window.L.circleMarker(latlngs[latlngs.length-1], { radius:8, color:'white', fillColor:'#E24B4A', fillOpacity:1, weight:2 })
          .addTo(map).bindPopup(`<b>End</b><br/>${pts[pts.length-1].gpsTime || ''}`);

        // Store layer group for cleanup
        routeLayerRef.current = window.L.layerGroup([line]).addTo(map);
        map.fitBounds(line.getBounds(), { padding:[40,40] });
      }
    } catch {}
    setLoadingRoute(false);
  };

  // Route playback animation
  const togglePlay = () => {
    if (playing) {
      clearInterval(playIntervalRef.current);
      setPlaying(false);
    } else {
      setPlaying(true);
      playIntervalRef.current = setInterval(() => {
        setPlaybackIdx(prev => {
          const next = prev + 1;
          if (next >= tracks.length) { clearInterval(playIntervalRef.current); setPlaying(false); return prev; }
          // Move map to current point
          if (mapInstanceRef.current && tracks[next]) {
            mapInstanceRef.current.panTo([parseFloat(tracks[next].lat), parseFloat(tracks[next].lng)], { animate:true, duration:0.5 });
          }
          return next;
        });
      }, 300);
    }
  };

  const focusVehicle = (id) => {
    const pos = positions.find(p => p.vehiIdno === id);
    if (pos && mapInstanceRef.current) {
      mapInstanceRef.current.setView([pos.lat, pos.lng], 15);
      markersRef.current[id]?.openPopup();
    }
    setSelected(id);
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:12, height:'calc(100vh - 110px)' }}>
      {/* Left panel */}
      <div style={{ background:'white', border:'0.5px solid #e8e8e8', borderRadius:12, overflow:'hidden', display:'flex', flexDirection:'column' }}>
        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'0.5px solid #eee' }}>
          {['live','route'].map(t => (
            <div key={t} onClick={() => setTab(t)} style={{ flex:1, padding:'10px 0', textAlign:'center', fontSize:12, fontWeight:tab===t?500:400, color:tab===t?'#185FA5':'#888', borderBottom:tab===t?'2px solid #185FA5':'2px solid transparent', cursor:'pointer', textTransform:'capitalize' }}>
              {t === 'live' ? 'Live tracking' : 'Route playback'}
            </div>
          ))}
        </div>

        {tab === 'live' ? (
          <div style={{ flex:1, overflowY:'auto' }}>
            <div style={{ padding:'8px 12px', fontSize:11, color:'#aaa', borderBottom:'0.5px solid #f5f5f5' }}>
              {positions.length} vehicle{positions.length !== 1?'s':''} on map · auto-refresh 15s
            </div>
            {vehicles.map((v, i) => {
              const id = v.devId || v.vehiIdno || v.id;
              const pos = positions.find(p => p.vehiIdno === id);
              return (
                <div key={i} onClick={() => focusVehicle(id)} style={{ padding:'10px 12px', borderBottom:'0.5px solid #f5f5f5', cursor:'pointer', background:selected===id?'#E6F1FB':'transparent' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:7, height:7, borderRadius:'50%', background:pos?.online?'#4CAF50':'#E24B4A', flexShrink:0 }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:500 }}>{v.displayName || id}</div>
                      <div style={{ fontSize:10, color:'#aaa' }}>
                        {pos ? (pos.online ? `${Math.round(pos.speed)} km/h` : 'Offline') : 'No GPS'}
                      </div>
                    </div>
                    <svg onClick={(e) => { e.stopPropagation(); focusVehicle(id); }} width="13" height="13" fill="#aaa" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ flex:1, display:'flex', flexDirection:'column', padding:12, gap:10, overflowY:'auto' }}>
            <div>
              <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>Vehicle</div>
              <select value={selected||''} onChange={e => setSelected(e.target.value)} style={{ width:'100%', fontSize:12, padding:'6px 8px', border:'0.5px solid #ddd', borderRadius:6 }}>
                <option value="">Select vehicle</option>
                {vehicles.map((v,i) => <option key={i} value={v.devId||v.vehiIdno||v.id}>{v.displayName||`Vehicle ${i+1}`}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>From</div>
              <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ width:'100%', fontSize:12, padding:'6px 8px', border:'0.5px solid #ddd', borderRadius:6 }} />
            </div>
            <div>
              <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>To</div>
              <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ width:'100%', fontSize:12, padding:'6px 8px', border:'0.5px solid #ddd', borderRadius:6 }} />
            </div>
            <button onClick={loadRoute} disabled={!selected||loadingRoute} style={{ padding:'8px', background: !selected?'#ccc':'#185FA5', color:'white', border:'none', borderRadius:6, fontSize:12, cursor:selected?'pointer':'default', fontWeight:500 }}>
              {loadingRoute ? 'Loading...' : 'Show route'}
            </button>

            {tracks.length > 0 && (
              <div style={{ background:'#f7f8fa', borderRadius:8, padding:10 }}>
                <div style={{ fontSize:11, color:'#888', marginBottom:8 }}>{tracks.length} track points</div>
                <div style={{ fontSize:11, marginBottom:6 }}>
                  <span style={{ color:'#888' }}>Point: </span>
                  <span style={{ fontWeight:500 }}>{playbackIdx + 1} / {tracks.length}</span>
                </div>
                {tracks[playbackIdx] && (
                  <div style={{ fontSize:10, color:'#aaa', marginBottom:8 }}>
                    {tracks[playbackIdx].gpsTime || ''} · {Math.round(tracks[playbackIdx].speed||0)} km/h
                  </div>
                )}
                <input type="range" min={0} max={tracks.length-1} step={1} value={playbackIdx}
                  onChange={e => {
                    const idx = Number(e.target.value); setPlaybackIdx(idx);
                    if (mapInstanceRef.current && tracks[idx]) mapInstanceRef.current.panTo([parseFloat(tracks[idx].lat), parseFloat(tracks[idx].lng)]);
                  }}
                  style={{ width:'100%', marginBottom:8 }} />
                <button onClick={togglePlay} style={{ width:'100%', padding:'6px', background: playing?'#E24B4A':'#185FA5', color:'white', border:'none', borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:500 }}>
                  {playing ? 'Pause' : 'Play route'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ borderRadius:12, overflow:'hidden', position:'relative', border:'0.5px solid #e8e8e8' }}>
        <div ref={mapRef} style={{ width:'100%', height:'100%' }} />
        {!mapReady && (
          <div style={{ position:'absolute', inset:0, background:'#f4f6f9', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:8 }}>
            <div style={{ width:28, height:28, border:'2px solid #ddd', borderTopColor:'#185FA5', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
            <div style={{ fontSize:12, color:'#aaa' }}>Loading map...</div>
          </div>
        )}
        {/* Legend */}
        <div style={{ position:'absolute', bottom:16, right:16, background:'white', borderRadius:8, padding:'8px 12px', border:'0.5px solid #e8e8e8', fontSize:11, zIndex:1000 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#185FA5', display:'inline-block' }} /> Moving
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#4CAF50', display:'inline-block' }} /> Idle
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#E24B4A', display:'inline-block' }} /> Offline
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
